// The slow-mo zoom finisher (#118): when a big combo lands, the camera punches
// in (a narrower field of view) and the trick effects' clock slows, so the
// landing burst hangs in the air, then both ease back. Render-only: the sim
// keeps its fixed step, so trajectories, scores and sounds never change. Plain
// numbers, no three.js, so it's testable; `Game` feeds the zoom to the chase
// camera and the effect clock to `TrickFx`.

/** How long the whole move lasts, in seconds. */
export const SLOWMO_TIME = 1.4;
/** How much of the field of view the punch-in takes away at its peak. */
export const SLOWMO_ZOOM = 0.3;
/** The effects' clock at the slowest. */
export const SLOWMO_FX_SCALE = 0.25;
/** The share of the move spent punching in; the rest eases back out. */
const IN = 0.2;

export class FinisherCam {
  /** Seconds into the move, or -1 when idle. */
  private t = -1;
  /** 0 idle .. 1 at the peak. */
  private k = 0;

  get active(): boolean {
    return this.t >= 0;
  }

  /** A big combo landed: start (or start over). */
  start(): void {
    this.t = 0;
  }

  /** Drops the move (a new map, a respawn). */
  reset(): void {
    this.t = -1;
    this.k = 0;
  }

  /** One rendered frame, in real seconds. */
  step(dt: number): void {
    if (this.t < 0) return;
    this.t += dt;
    if (this.t >= SLOWMO_TIME) {
      this.reset();
      return;
    }
    const u = this.t / SLOWMO_TIME;
    // Quick in, a long smooth ease back out.
    const k = u < IN ? u / IN : 1 - (u - IN) / (1 - IN);
    this.k = k * k * (3 - 2 * k);
  }

  /** How far in the camera is, 0 .. `SLOWMO_ZOOM`. */
  get zoom(): number {
    return this.k * SLOWMO_ZOOM;
  }

  /** What to multiply the effects' frame time by: 1 normally, `SLOWMO_FX_SCALE` at the peak. */
  get fxScale(): number {
    return 1 - this.k * (1 - SLOWMO_FX_SCALE);
  }

  /** How hard the depth of field (#179) blurs off the rider: 0 idle .. 1 at the peak. */
  get focus(): number {
    return this.k;
  }

  /** The camera's field of view for a rig's `base`. */
  fov(base: number): number {
    return base * (1 - this.zoom);
  }
}
